import {
    AccountWorkspaceDescriptor,
    WorkspaceDescriptor,
} from '@synapex/shared2/entities'
import {
    CreateVkOrdApiKeyInputDto,
    UpdateVkOrdApiKeyInputDto,
    VkOrdApiKeyResponse,
} from '@synapex/shared2/gateway-rest-api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiQueryKey } from '../query-key'
import {
    copyVkOrdApiKey,
    deleteUserVkOrdKey,
    setUserVkOrdKey,
    updateUserAdditionalOrdData,
} from './ord.service'

export function useMutationVkOrdKey() {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, CreateVkOrdApiKeyInputDto>({
        mutationFn: (input) => setUserVkOrdKey(input),
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.additionalOrdData(variables),
            })
        },
        onError: () => {
            // TODO: show error toaster
        },
    })
}

export function useDeleteVkOrdKey() {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, WorkspaceDescriptor>({
        mutationFn: (wd) => deleteUserVkOrdKey(wd),
        onSuccess: (_, wd) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.additionalOrdData(wd),
            })
        },
        onError: () => {
            // TODO: show error toaster
        },
    })
}

export function useUpdateAdditionalOrdData() {
    const queryClient = useQueryClient()

    type AllReplyData = {
        advertiserAccounts: Array<{
            advertiser_account_id: number
            ordAdditionalData: VkOrdApiKeyResponse | null
            [key: string]: unknown
        }>
        [key: string]: unknown
    }

    return useMutation<
        VkOrdApiKeyResponse,
        Error,
        {
            awd: AccountWorkspaceDescriptor
            input: UpdateVkOrdApiKeyInputDto
        },
        {
            previousData: AllReplyData | undefined
            queryKey: ReturnType<typeof apiQueryKey.additionalOrdData>
        }
    >({
        mutationFn: ({ awd, input }) => updateUserAdditionalOrdData(awd, input),
        onMutate: async ({ awd, input }) => {
            const queryKey = apiQueryKey.additionalOrdData(awd)

            await queryClient.cancelQueries({ queryKey })

            const previousData =
                queryClient.getQueryData<AllReplyData>(queryKey)

            if (previousData && awd.advertiser_account_id) {
                queryClient.setQueryData<AllReplyData>(queryKey, {
                    ...previousData,
                    advertiserAccounts: previousData.advertiserAccounts.map(
                        (acc) =>
                            acc.advertiser_account_id ===
                            awd.advertiser_account_id
                                ? {
                                      ...acc,
                                      ordAdditionalData: acc.ordAdditionalData
                                          ? {
                                                ...acc.ordAdditionalData,
                                                adSiteUrl:
                                                    input.adSiteUrl ??
                                                    acc.ordAdditionalData
                                                        .adSiteUrl,
                                                adOrdContragentId:
                                                    input.adOrdContragentId ??
                                                    acc.ordAdditionalData
                                                        .adOrdContragentId,
                                                adOrdAgencyId:
                                                    input.adOrdAgencyId ??
                                                    acc.ordAdditionalData
                                                        .adOrdAgencyId,
                                                currentLinkType:
                                                    input.currentLinkType ??
                                                    acc.ordAdditionalData
                                                        .currentLinkType,
                                            }
                                          : null,
                                  }
                                : acc
                    ),
                })
            }

            return { previousData, queryKey }
        },
        onError: (_error, _variables, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(context.queryKey, context.previousData)
            }
        },
        onSuccess: (_, { awd }) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.additionalOrdData(awd),
            })
        },
    })
}

export function useCopyVkOrdApiKey() {
    return useMutation<string, Error, WorkspaceDescriptor>({
        mutationFn: (wd) => copyVkOrdApiKey(wd),
    })
}
